import React, { forwardRef, useEffect, useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { api } from '../api';
import { useAuth } from '../units/auth/AuthProvider';
import { Backdrop, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { SideBar, useSideBar } from './SideBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBarcodeRead, faBars, faBell, faMagnifyingGlass, faUser } from '@fortawesome/pro-regular-svg-icons';
import { SimpleAlert, useSimpleAlert } from './SimpleAlert';
import { Alert, useAlert } from './Alert';
import { useScreenSize } from '../hooks/useScreenSize';
import { blue, lime } from '@mui/material/colors';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: blue[50],
        },
    }),
);

export const usePage = () => {
    const classes = useStyles();
    const auth = useAuth();
    const screenSize = useScreenSize();
    const sideBar = useSideBar();
    const simpleAlert = useSimpleAlert();
    const alert = useAlert();
    const [isLoading, setIsLoading] = useState(false);
    const [navHeight, setNavHeight] = useState(50);
    const [sidebarWidth, setSidebarWidth] = useState(210);
    const [sidebarLeft, setSidebarLeft] = useState(screenSize.mdUp ? 0 : -210);
    useEffect(() => {
        api.auth.profile().then((ret) => {
            if (ret.statusCode == 401) auth.signOut();
        });
    }, []);
    return {
        classes,
        auth,
        sideBar,
        simpleAlert,
        alert,
        isLoading,
        setIsLoading,
        navHeight,
        setNavHeight,
        sidebarWidth,
        setSidebarWidth,
        sidebarLeft,
        setSidebarLeft,
    };
};

export interface PageProps extends ReturnType<typeof usePage> {
    title: string;
    children: React.ReactNode;
}

const Page = forwardRef<HTMLDivElement, PageProps>((props, ref): React.ReactElement => {
    useEffect(() => {
        window.document.title = props.title;
    }, [props.title]);
    return (
        <div style={{ width: '100vw', height: '100vh', position: 'relative' }}>
            <SimpleAlert {...props.simpleAlert} />
            <Backdrop open={props.isLoading} sx={{ zIndex: 100 }}>
                <CircularProgress sx={{ position: 'relative', left: (props.sidebarLeft + props.sidebarWidth) / 2 }} />
            </Backdrop>
            <Alert {...props.alert} />
            <div
                style={{
                    width: '100vw',
                    height: props.navHeight,
                    backgroundColor: lime[50],
                    position: 'fixed',
                    top: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Grid container item>
                    <Grid container item flex={1} justifyContent={'flex-start'} alignItems={'center'}>
                        <Button
                            onClick={() => {
                                if (props.sidebarLeft == 0) {
                                    props.setSidebarLeft(0 - props.sidebarWidth);
                                } else {
                                    props.setSidebarLeft(0);
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faBars} fixedWidth color={'#000000'} />
                        </Button>
                    </Grid>
                    <Grid container item flex={1} justifyContent={'center'} alignItems={'center'}>
                        <Typography variant='h6'>{props.title}</Typography>
                    </Grid>
                    <Grid
                        container
                        item
                        flex={1}
                        justifyContent={'flex-end'}
                        alignItems={'center'}
                        gap={1}
                        paddingRight={1}
                        sx={{ '& img': { maxHeight: props.navHeight * 0.8 } }}
                    >
                        {props.auth.profile.userName}
                        <img src={props.auth.profile.picture} alt='' />
                    </Grid>
                </Grid>
            </div>
            <div
                style={{
                    width: props.sidebarWidth,
                    height: `calc(100vh - ${props.navHeight}px)`,
                    backgroundColor: '#284D5D',
                    position: 'absolute',
                    top: props.navHeight,
                    left: props.sidebarLeft,
                    overflow: 'auto',
                    transition: 'all 0.3s ease',
                }}
            >
                <SideBar {...props.sideBar} />
            </div>
            <div
                ref={ref}
                style={{
                    width: `calc(100vw - ${props.sidebarWidth}px - ${props.sidebarLeft}px)`,
                    height: `calc(100vh - ${props.navHeight}px)`,
                    backgroundColor: blue[50],
                    position: 'absolute',
                    top: props.navHeight,
                    left: props.sidebarLeft + props.sidebarWidth,
                    overflow: 'auto',
                    transition: 'all 0.3s ease',
                }}
            >
                {props.children}
            </div>
        </div>
    );
});

export default Page;
